/**
 * Adapted from swamimalode07/rare-ui's GitHub Activity (MIT).
 * Calendar only: supplied organization data, primary tokens, keyboard navigation.
 * Attribution and full license: github-activity.LICENSE.
 */
import { useId, useMemo, useRef, useState, type FocusEvent, type PointerEvent } from "react";
import { createPortal } from "react-dom";
import { cn } from "~/lib/utils";
import { activityDayLabel, activityFocusDate, activityLevel, activityMonthLabels, activityWeeks, type ActivityDay } from "./github-activity-calendar";

const LEVELS = ["bg-muted", "bg-primary/25", "bg-primary/45", "bg-primary/70", "bg-primary"] as const;

export function GitHubActivity({ days, to, months, className, maxCellSize }: {
  readonly days: ReadonlyArray<ActivityDay>;
  readonly to: string;
  readonly months: 6 | 12;
  readonly className?: string;
  /** Caps square cells in fixed-height dashboard cards without narrowing the calendar. */
  readonly maxCellSize?: number;
}) {
  const weeks = useMemo(() => activityWeeks(days, to, months), [days, to, months]);
  const labels = useMemo(() => activityMonthLabels(weeks), [weeks]);
  const visibleDays = weeks.flat().filter((day): day is ActivityDay => day !== null);
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const [focused, setFocused] = useState<string | null>(null);
  const [hovered, setHovered] = useState<{ day: ActivityDay; x: number; y: number } | null>(null);
  const tooltipId = useId();
  const hoveredDay = hovered ? days.find((day) => day.date === hovered.day.date) : undefined;
  const show = (day: ActivityDay, event: PointerEvent<HTMLButtonElement> | FocusEvent<HTMLButtonElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    setHovered({ day, x: Math.min(Math.max(box.left + box.width / 2, 120), window.innerWidth - 120), y: box.top });
  };
  const tabDate = visibleDays.some((day) => day.date === focused) ? focused : visibleDays.at(-1)?.date;

  return (
    <div className={cn("w-full min-w-0", className)} data-slot="github-activity">
      <div className="overflow-x-auto pb-2" onScroll={() => setHovered(null)}>
        <div className="grid w-full gap-x-px gap-y-1 sm:gap-x-[3px]" role="group" aria-label="Inspection calendar. Use arrow keys to explore dates."
          style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` }}>
          {labels.map((month, index) => <div key={`month-${index}`} className="relative h-5 text-[10px] text-muted-foreground">
            {month ? <span className="absolute left-0">{month}</span> : null}
          </div>)}
          {weeks.map((week, weekIndex) => <div key={weekIndex} className="grid grid-rows-7 gap-px sm:gap-[3px]">
            {week.map((day, index) => day === null ? <span key={`blank-${index}`} aria-hidden className="aspect-square w-full" style={{ maxWidth: maxCellSize }} /> : (
              <button key={day.date} type="button"
                ref={(element) => { if (element) buttons.current.set(day.date, element); else buttons.current.delete(day.date); }}
                style={{ maxWidth: maxCellSize }}
                data-date={day.date} data-count={day.count} data-level={activityLevel(day.count)}
                aria-label={activityDayLabel(day)} aria-describedby={hovered?.day.date === day.date ? tooltipId : undefined}
                tabIndex={day.date === tabDate ? 0 : -1}
                className={cn("aspect-square w-full rounded-[3px] outline-offset-2 transition-[filter] hover:brightness-90 focus-visible:outline-2 focus-visible:outline-ring motion-reduce:transition-none", LEVELS[activityLevel(day.count)])}
                onPointerEnter={(event) => show(day, event)}
                onPointerLeave={(event) => { if (document.activeElement !== event.currentTarget) setHovered(null); }}
                onFocus={(event) => { setFocused(day.date); show(day, event); }}
                onBlur={() => setHovered(null)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") { setHovered(null); return; }
                  const next = activityFocusDate(weeks, day.date, event.key);
                  if (next === undefined) return;
                  event.preventDefault();
                  if (next !== null) buttons.current.get(next)?.focus();
                }}
              />
            ))}
          </div>)}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 text-[10px] text-muted-foreground">
        <span>Each square is a day · UTC</span>
        <span className="flex shrink-0 items-center gap-1.5" aria-label="Legend: darker means more checks">
          Less {LEVELS.map((level, index) => <span key={level} aria-hidden data-level={index} className={cn("size-2.5 rounded-[2px]", level)} />)} More
        </span>
      </div>
      {hovered && hoveredDay && createPortal(<div id={tooltipId} role="tooltip" className="pointer-events-none fixed z-[150] max-w-60 -translate-x-1/2 -translate-y-full rounded-md bg-foreground px-2.5 py-1.5 text-center text-[11px] text-background shadow-sm"
        style={{ left: hovered.x, top: hovered.y - 8 }}>{activityDayLabel(hoveredDay)}</div>, document.body)}
    </div>
  );
}
